import Button from 'common/components/Button';
import ErrorComponent from 'common/components/Error';
import PageHeader from 'common/components/PageHeader';
import AuthenticatedLayout from 'common/layouts/AuthenticatedLayout';
import appService from 'common/services/apps';
import developerRequests from 'common/services/developerRequests';
import { DeveloperRequestPayload } from 'common/types/developerRequests';
import React from 'react';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import styles from '../api-platform-requests/index.module.scss';

interface SupportFormValues {
  question: string;
  clientId: string;
}

export function Support() {
  const navigate = useNavigate();

  // The Support page has no app context, so offer the developer's apps as an
  // optional dropdown to associate the question with one of their apps.
  const appsTask = useAsync(appService.getApps, []);
  const apps = appsTask.result;

  const submitTask = useAsyncCallback(
    async (payload: DeveloperRequestPayload) => {
      await developerRequests.submitDeveloperRequest(payload);
    },
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SupportFormValues>({
    mode: 'onChange',
    defaultValues: { question: '', clientId: '' },
  });

  const onSubmit = (values: SupportFormValues) => {
    submitTask.execute({
      requestType: 'SUPPORT_QUESTION',
      question: values.question,
      // client_id is optional for support; only sent when an app is selected.
      clientId: values.clientId || undefined,
    });
  };

  if (submitTask.status === 'success') {
    return (
      <>
        <PageHeader title="Support" />
        <div className={styles.root} data-testid="support-submitted">
          <h2 className={styles.stepTitle}>Thank you</h2>
          <p className={styles.intro}>
            Your question has been submitted. We will contact you regarding your
            request at the email address associated with your WHOOP account as
            soon as we are able. We appreciate your patience.
          </p>
          <div className={styles.nav}>
            <Button
              variant="primary"
              theme="dark"
              onClick={() => navigate('/')}
              data-testid="support-done-button"
            >
              Done
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Support" />
      <div className={styles.root} data-testid="support-form">
        <div className={styles.field}>
          <label className={styles.label} htmlFor="question">
            <span>
              What is your question?<span className={styles.required}>*</span>
            </span>
            <textarea
              id="question"
              data-testid="field-question"
              className={`${styles.formInput} ${styles.textArea} ${
                errors.question ? styles.inputError : ''
              }`.trim()}
              {...register('question', {
                required: 'This field is required',
              })}
            />
          </label>
          {errors.question && (
            <p className={styles.errorMessage}>{errors.question.message}</p>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="clientId">
            <span>Which app is this about? (optional)</span>
            <select
              id="clientId"
              data-testid="field-clientId"
              className={styles.formInput}
              {...register('clientId')}
            >
              <option value="">No specific app</option>
              {apps?.map((app) => (
                <option key={app.clientId} value={app.clientId}>
                  {app.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {submitTask.error && (
          <ErrorComponent>{submitTask.error.message}</ErrorComponent>
        )}

        <div className={styles.nav}>
          <Button
            variant="normal"
            theme="dark"
            onClick={() => navigate('/')}
            data-testid="support-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            theme="dark"
            disabled={submitTask.loading}
            onClick={handleSubmit(onSubmit)}
            data-testid="support-submit"
          >
            {submitTask.loading ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </div>
    </>
  );
}

export default () => (
  <AuthenticatedLayout pageIdentifier="support">
    <Support />
  </AuthenticatedLayout>
);
